import { BluetoothLowEnergyAdapterProvider } from './adapter/BluetoothLowEnergyAdapterProvider';
import { BluetoothLowEnergyNamespaces } from './adapter/AbstractBluetoothLowEnergyAdapter';
import { BluetoothLowEnergyEventChannel, IBluetoothLowEnergyEvent, IBluetoothLowEnergyEventSubscription } from './BluetoothLowEnergyEventChannel';

const NAMESPACE = BluetoothLowEnergyNamespaces.ADAPTER;

/**
 * The Bluetooth radio itself: whether it is there, what state it is in, and whether this tool holds
 * the permissions it needs. Here "adapter" means the radio, not the adapter pattern.
 *
 * Start here before scanning or advertising. Two things this service reports cannot be discovered any
 * other way:
 *
 * - Peripheral support is separate from Bluetooth Low Energy support. Plenty of hardware can only act
 *   as a central, and advertising fails there however the radio is configured.
 * - Below Android 12, scanning needs system location services switched on as well as the permission.
 *   With them off a scan finds nothing and reports no error at all, so a tool that does not check
 *   looks broken for a reason the user cannot see.
 *
 * Only available inside the CandyCane application. On the Velocity browser every method rejects.
 *
 * ## Errors
 *
 * A rejection carries `{ code, message }`. Codes shared by every Bluetooth role:
 * `INVALID_OPTIONS` (a required option is absent, blank, or an enum token is unrecognised),
 * `UNKNOWN_METHOD`, and — from the bridge router rather than this role — `INITIALIZATION_ERROR`
 * (the provider could not be constructed) and `PLUGIN_NOT_READY`.
 *
 * Codes specific to this role: `SUPPORT_CHECK_FAILED`, `ADAPTER_STATE_FAILED`,
 * `PERMISSION_CHECK_FAILED`, `PERMISSION_REQUEST_FAILED`, `LOCATION_SERVICES_CHECK_FAILED`,
 * `LISTENER_REGISTRATION_FAILED`, `LISTENER_UNREGISTRATION_FAILED`.
 */
export class BluetoothLowEnergyAdapterService {
    /**
     * Whether this device supports Bluetooth Low Energy at all.
     *
     * @return {Promise} resolves `{ supported: boolean }`.
     */
    public static isSupported(): Promise<any> {
        return BluetoothLowEnergyAdapterProvider.get().call(NAMESPACE, 'isSupported');
    }

    /**
     * Whether this device can act as a peripheral, which is a narrower question than
     * {@link isSupported}.
     *
     * @return {Promise} resolves `{ supported: boolean }`.
     */
    public static isPeripheralModeSupported(): Promise<any> {
        return BluetoothLowEnergyAdapterProvider.get().call(NAMESPACE, 'isPeripheralModeSupported');
    }

    /**
     * The radio's current state.
     *
     * @return {Promise} resolves `{ state: 'ON' | 'OFF' | 'TURNING_ON' | 'TURNING_OFF' }`.
     */
    public static getState(): Promise<any> {
        return BluetoothLowEnergyAdapterProvider.get().call(NAMESPACE, 'getState');
    }

    /**
     * Starts reporting radio state changes as `adapterState` events, so a tool learns the user has
     * switched Bluetooth off directly rather than by watching an operation fail.
     *
     * Open the event channel first. Calling this again replaces the listener rather than adding a
     * second one, and a change landing during that swap can be reported twice — read the state carried
     * by the event rather than counting transitions.
     *
     * @return {Promise} resolves `{ registered: true }`.
     */
    public static registerStateListener(): Promise<any> {
        return BluetoothLowEnergyAdapterProvider.get().call(NAMESPACE, 'registerStateListener');
    }

    /**
     * Stops reporting radio state changes.
     *
     * @return {Promise} resolves `{ unregistered: true }`.
     */
    public static unregisterStateListener(): Promise<any> {
        return BluetoothLowEnergyAdapterProvider.get().call(NAMESPACE, 'unregisterStateListener');
    }

    /**
     * Whether every named use is already permitted. A use is named rather than a permission because
     * the underlying permission names changed at Android 12.
     *
     * @param config `{ usages: ('SCAN' | 'CONNECT' | 'ADVERTISE')[] }`, non-empty.
     * @return {Promise} resolves `{ granted: boolean }`, true only if every named use is permitted.
     */
    public static hasPermissions(config: any): Promise<any> {
        return BluetoothLowEnergyAdapterProvider.get().call(NAMESPACE, 'hasPermissions', config);
    }

    /**
     * Prompts for the permissions the named uses need, resolving once the user has answered. Resolves
     * immediately when they are already held.
     *
     * @param config `{ usages: ('SCAN' | 'CONNECT' | 'ADVERTISE')[] }`, non-empty.
     * @return {Promise} resolves `{ granted: boolean }`.
     */
    public static requestPermissions(config: any): Promise<any> {
        return BluetoothLowEnergyAdapterProvider.get().call(NAMESPACE, 'requestPermissions', config);
    }

    /**
     * Whether this device needs system location services on for scanning to return results. True below
     * Android 12.
     *
     * @return {Promise} resolves `{ required: boolean }`.
     */
    public static isLocationServicesRequiredForScanning(): Promise<any> {
        return BluetoothLowEnergyAdapterProvider.get().call(NAMESPACE, 'isLocationServicesRequiredForScanning');
    }

    /**
     * Whether system location services are on. Worth checking before every scan on a device where
     * {@link isLocationServicesRequiredForScanning} is true: this is the one precondition whose
     * absence a scan does not report.
     *
     * @return {Promise} resolves `{ enabled: boolean }`.
     */
    public static isLocationServicesEnabled(): Promise<any> {
        return BluetoothLowEnergyAdapterProvider.get().call(NAMESPACE, 'isLocationServicesEnabled');
    }

    /**
     * Starts receiving this role's events and delivers each to `handler`.
     *
     * One event type: `adapterState`, with payload `{ state }` carrying the same token set as
     * {@link getState}.
     *
     * @param handler invoked once per event.
     * @return {Promise} resolves a subscription whose `off()` detaches the handler. Prefer `off()` over
     *   {@link closeEventChannel} for per-component cleanup — closing is tool-level and stops the
     *   stream for every subscriber in this frame.
     */
    public static openEventChannel(handler: (event: IBluetoothLowEnergyEvent) => void): Promise<IBluetoothLowEnergyEventSubscription> {
        return BluetoothLowEnergyEventChannel.open(NAMESPACE, handler);
    }

    /**
     * Stops receiving this role's events and detaches every handler.
     *
     * @return {Promise} resolves `{ closed: boolean }`, false when no channel was open.
     */
    public static closeEventChannel(): Promise<any> {
        return BluetoothLowEnergyEventChannel.close(NAMESPACE);
    }
}
